import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isEqual, isSameDay, isValid, parse, startOfDay } from "date-fns";
import { defineMessages, useIntl } from "react-intl";

import { Dropdown, FormItem, TimePicker } from "@mapmycustomers/ui";

import FormValues from "@app/component/createEditEntity/Activity/types/FormValues";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

import styles from "../../ActivityTime.module.scss";
import useMenu from "../../useMenu";
import ActivityEndDate from "../ActivityEndDate";

const INTERVAL_MINUTES = 30;

const messages = defineMessages({
  changeEndTime: {
    id: "createActivityModal.footer.scheduleActivity.changeEndTime",
    defaultMessage: "Change End Time",
    description: "Change End Time",
  },
  removeEndDateTime: {
    id: "createActivityModal.footer.scheduleActivity.endTime.remove",
    defaultMessage: "Remove",
    description: "Select Another Time",
  },
  selectAnotherTime: {
    id: "createActivityModal.footer.scheduleActivity.selectAnotherTime",
    defaultMessage: "Select Another Time",
    description: "Select Another Time",
  },
  selectEndTime: {
    id: "createActivityModal.footer.scheduleActivity.selectEndTime",
    defaultMessage: "Select End Time",
    description: "Select End Time",
  },
  setTime: {
    id: "createActivityModal.footer.scheduleActivity.setEndTime",
    defaultMessage: "Set time",
    description: "Set time text",
  },
});

interface ActivityEndTimeProps {
  disabledTooltip?: string;
  form: FormInstance<FormValues>;
  onChange?: () => void;
  onOpenChange?: (value: boolean) => void;
}

const ActivityEndTime = ({
  disabledTooltip,
  form,
  onChange,
  onOpenChange,
}: ActivityEndTimeProps) => {
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const startDate = useWatch(ActivityFieldName.START_AT, form);
  const endDate = useWatch(ActivityFieldName.END_AT, form);
  const isSameDate = !startDate || !endDate || isSameDay(startDate, endDate);

  const handleEndOpenChanged = useCallback(
    (open: boolean) => {
      if (open) {
        setTooltipVisible(false);
      }

      setOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const handleEndClick = useCallback(() => {
    setTooltipVisible(false);
    setOpen(true);
  }, []);

  const handleEndDateChange = useCallback(() => {
    onChange?.();
  }, [onChange]);

  const timePickerFormat = SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm";

  const handleTimeChange = useCallback(
    (date: Date) => {
      form.setFieldValue(ActivityFieldName.END_AT, date);
      setOpen(false);
    },
    [form]
  );

  const startDateForIntervals = useMemo(() => {
    if (startDate && ((endDate && isSameDay(startDate, endDate)) || !endDate)) {
      return startDate;
    }
    if (endDate) {
      return startOfDay(endDate);
    }
    return new Date();
  }, [endDate, startDate]);

  const menu = useMenu(
    startDateForIntervals,
    INTERVAL_MINUTES,
    handleTimeChange,
    endDate,
    isSameDate
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (!value) {
        return;
      }

      const updatedDate = parse(value, timePickerFormat, startDateForIntervals);

      if (endDate && isEqual(updatedDate, endDate)) {
        return;
      }

      if (isValid(updatedDate)) {
        setTimeout(() => {
          form.setFieldValue(ActivityFieldName.END_AT, updatedDate);
        });
      }
    },
    [endDate, form, startDateForIntervals, timePickerFormat]
  );

  const handleMenuContainerMouseDown = useCallback((e: MouseEvent) => {
    // prevent calling on blur of TimePicker when clicking on menu
    e.preventDefault();
  }, []);

  return (
    <>
      <span className={styles.separator}>&ndash;</span>

      <ActivityEndDate
        disabledTooltip={disabledTooltip}
        form={form}
        onChange={handleEndDateChange}
      />

      <Tooltip
        destroyTooltipOnHide
        mouseLeaveDelay={0}
        onOpenChange={setTooltipVisible}
        open={tooltipVisible}
        overlayClassName={styles.tooltip}
        placement="top"
        title={
          disabledTooltip ??
          (endDate
            ? intl.formatMessage(messages.changeEndTime)
            : intl.formatMessage(messages.selectEndTime))
        }
        trigger={open ? [] : ["hover"]}
      >
        <div>
          <Dropdown
            disabled={!!disabledTooltip}
            dropdownRender={(menu) => <div onMouseDown={handleMenuContainerMouseDown}>{menu}</div>}
            menu={menu}
            onOpenChange={handleEndOpenChanged}
            open={open}
            placement="bottom"
            trigger={disabledTooltip ? [] : ["click"]}
          >
            <FormItem name={ActivityFieldName.END_AT} noStyle showLabel={false}>
              <TimePicker
                className={cn(styles.timePicker, { [styles.allowClear]: !!endDate })}
                disabled={!!disabledTooltip}
                dropdownClassName={styles.overlay}
                format={timePickerFormat}
                onBlur={handleBlur}
                onChange={() => setOpen(false)}
                onClick={disabledTooltip ? undefined : handleEndClick}
                open
                placeholder={intl.formatMessage(messages.setTime)}
                showSecond={false}
                size="middle"
                suffixIcon={<></>}
                use12Hours={SHOULD_USE_12_HOUR_FORMAT}
              />
            </FormItem>
          </Dropdown>
        </div>
      </Tooltip>
    </>
  );
};

export default ActivityEndTime;
